import { AppErrorBoundary } from '@/components/app-error-boundary.component';
import { PushNotificationProvider } from '@/components/push-notification-provider.component';
import { LocalizedMuiProviders } from '@/components/providers/localized-mui-providers.component';
import i18n from '@/lib/i18n';
import { queryClient } from '@/lib/react-query';
import { spaceGrotesk } from '@/theme/fonts';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import type React from 'react';

import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { FunctionComponent, useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

let posthogInitialized = false;

function initializePostHog() {
  if (posthogInitialized) {
    return;
  }

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (key) {
    posthog.init(key, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true
    });
    posthogInitialized = true;
  }
}

const ClientSideOnly: FunctionComponent<{ children: React.ReactNode }> = ({
  children
}) => {
  const { isReady } = useRouter();
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(isReady);
  }, [isReady]);

  if (!hasMounted) {
    return null;
  }
  return <>{children}</>;
};

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  useEffect(() => {
    initializePostHog();
  }, []);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      return;
    }

    const handleRouteChange = (url: string) => {
      posthog.capture('$pageview', { $current_url: url });
    };

    posthog.capture('$pageview', { $current_url: window.location.href });

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <PostHogProvider client={posthog}>
      <ClientSideOnly key={pageProps.pathname}>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <LocalizedMuiProviders>
              <GlobalStyles
                styles={{
                  ':root': {
                    '--font-space-grotesk': spaceGrotesk.style.fontFamily
                  }
                }}
              />
              <CssBaseline />
              <PushNotificationProvider>
                <AppErrorBoundary>
                  <Component {...pageProps} />
                </AppErrorBoundary>
              </PushNotificationProvider>
            </LocalizedMuiProviders>
          </QueryClientProvider>
        </I18nextProvider>
      </ClientSideOnly>
    </PostHogProvider>
  );
}
